import { type BypassableValues, isTruthy } from "@vega-core/utils/isTruthy";

/**
 * Checks if the given value is empty or falsy.
 *
 * It returns true if the given value is one of the following:
 * - `""` (empty string)
 * - `0` (number, unless `bypass` is `BypassableValues.Zero`)
 * - `false` (boolean)
 * - `null`
 * - `undefined`
 *
 * Otherwise, it returns false.
 *
 * The main difference between this function and the `isTruthy` is that this
 * one returns true if the given value is `0` or `""`, unless bypassed.
 *
 * @example
 * isFalsy(0); // true
 * isFalsy("", BypassableValues.Zero); // true
 * isFalsy(null); // true
 * isFalsy(undefined); // true
 * isFalsy(false); // true
 *
 * @example
 * isFalsy(1); // false
 * isFalsy("a"); // false
 * isFalsy({}, BypassableValues.Zero); // false
 *
 * @param value - Any value to check
 * @param bypass - An optional parameter to bypass certain falsy checks.
 * @returns true if the given value is falsy, false otherwise
 */
export function isFalsy(
  value: unknown,
  bypass?: BypassableValues,
): value is "" | 0 | false | null | undefined {
  return !isTruthy(value, bypass);
}
